import { useMemo, useState } from "react";
import { computeRoi, ROI, usd0 } from "../../lib/roi";

/**
 * Anonymized real-world ROI case study. Numbers recompute live with team size;
 * the break-even is derived from the same figures shown in the table, so it can
 * never drift. No workspace is named.
 */
export function RoiCaseStudy() {
  const [users, setUsers] = useState(10);
  const r = useMemo(() => computeRoi(users), [users]);

  return (
    <section className="section" id="roi">
      <div className="container">
        <span className="eyebrow">Real-world ROI</span>
        <h2 className="h-section" style={{ margin: "14px 0 10px", maxWidth: "24ch" }}>
          A vacation-rental operation did the math.
        </h2>
        <p className="lede" style={{ marginBottom: 10, maxWidth: "62ch" }}>
          Five years on Slack: <b>17,000+ messages, ~35 GB of photos &amp; video,
          20 channels.</b> They wanted to add seasonal, part-time help — but Slack
          charges per seat every month, even for someone who barely logs in. So
          they overpaid, or didn’t add the help at all. And on Slack’s free plan,
          their history vanishes after 90 days.
        </p>

        <div className="roi-slider card">
          <label htmlFor="roi-users">
            Team size <b>{r.users} {r.users === 1 ? "person" : "people"}</b>
          </label>
          <input
            id="roi-users"
            type="range"
            min={3}
            max={100}
            step={1}
            value={users}
            onChange={(e) => setUsers(Number(e.target.value))}
          />
        </div>

        <div className="roi-table-wrap">
          <table className="roi-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Monthly</th>
                <th>Per year</th>
                <th>Keeps 5 yrs of history?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Slack Free</td>
                <td>{usd0(0)}</td>
                <td>{usd0(0)}</td>
                <td className="roi-no">✕ gone after 90 days</td>
              </tr>
              <tr>
                <td>Slack Pro <span className="roi-rate">${ROI.slackProPerUserMonth}/user</span></td>
                <td>{usd0(r.proMonthly)}</td>
                <td>{usd0(r.proYear)}</td>
                <td className="roi-yes">✓ while you keep paying</td>
              </tr>
              <tr>
                <td>Slack Business+ <span className="roi-rate">${ROI.slackBizPerUserMonth}/user</span></td>
                <td>{usd0(r.bizMonthly)}</td>
                <td>{usd0(r.bizYear)}</td>
                <td className="roi-yes">✓ unlimited retention</td>
              </tr>
              <tr className="roi-featured">
                <td>Discord + Slackcord <span className="roi-rate">flat boost</span></td>
                <td>{usd0(ROI.boostMonthly)}</td>
                <td>{usd0(r.boostYear)}</td>
                <td className="roi-yes">✓ you own it forever</td>
              </tr>
            </tbody>
          </table>
          <p className="roi-fine mono">
            Slack AI is an extra per-seat add-on on top of any paid plan. List
            prices; billed annually.
          </p>
        </div>

        <div className="roi-callouts">
          <div className="roi-callout card">
            <span className="eyebrow">The deal</span>
            <p>
              One-time <b>~{usd0(ROI.migrationOneTime)}</b> migration plus{" "}
              <b>{usd0(ROI.boostMonthly)}/mo flat</b> Discord boost — versus Slack
              Business+ at <b>{usd0(r.bizYear)}/year</b>, forever, growing with
              every seat.
            </p>
          </div>
          <div className="roi-callout card">
            <span className="eyebrow">Break-even</span>
            <p>
              {r.breakEvenMonths === null ? (
                <>At this team size Slack is already cheap — the win is retention and flat scaling.</>
              ) : (
                <>
                  The migration pays for itself in{" "}
                  <b>≈ {r.breakEvenMonths} months</b> ({r.breakEvenYears?.toFixed(1)}{" "}
                  years). Over {ROI.years} years: <b>{usd0(r.slackcordTotal)}</b>{" "}
                  vs <b>{usd0(r.slackBizTotal)}</b> on Slack —{" "}
                  <b className="gradient-text">save {usd0(r.savings)}</b>.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="card roi-kicker">
          <h3>Now add {ROI.helperCount} seasonal helpers.</h3>
          <p>
            Slack climbs to <b>{usd0(r.bizYearWithHelpers)}/year</b>. Discord stays{" "}
            <b className="gradient-text">{usd0(r.boostYear)} flat</b>. On Slack every
            part-timer costs <b>~{usd0(r.perHelperYear)}/year</b> — forever. On
            Discord + Slackcord they cost <b>$0</b>. Add all the help you want; it
            never changes your bill.
          </p>
          <p className="roi-kicker-time">
            And it’s not just money. On Slack you spend real time{" "}
            <b>rationing seats</b> — deciding who’s worth paying for, juggling guest
            accounts, second-guessing every hire. On Discord it’s flat, so you stop
            optimizing headcount and just <b>add everyone</b> — seasonal help,
            contractors, the new cleaner — and get back to running the business.
          </p>
        </div>
      </div>
    </section>
  );
}
